import React, { useState } from 'react';
import { Badge, Box, Button, Flex, Spinner, Text } from '@chakra-ui/react';
import { ListDashes } from 'phosphor-react';
import { useNavigate } from 'react-router-dom';
import { AnalyticsIcon, AnalyticsModal } from '../../../components/analytics';
import { DataGrid } from '../../../components/datagrid';
import { ModuleMaterialsManager } from '../../../components/ModuleMaterialsManager';
import { PageTitle } from '../../../components/PageTitle';
import { SearchFilter } from '../../../components/inputs/filters/search';
import { useFetch } from '../../../hooks/useFetch';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { usePageTracking } from '../../../hooks/usePageTracking';
import { useAuth } from '../../../providers/auth';
import { api } from '../../../services/api';
import { getCellStatus } from '../../../utils/FormatFunctions';

interface MaterialCounts {
  total: number;
  public: number;
  hidden: number;
}

function ModuleCounts({ moduleId, refreshKey }: { moduleId: number; refreshKey: number }) {
  const [counts, setCounts] = React.useState<MaterialCounts | null>(null);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    let active = true;
    setCounts(null);
    setFailed(false);
    api
      .get(`/v1/admin/extra_materials/modules/${moduleId}/counts`)
      .then((response) => {
        if (active) {
          setCounts(response.data?.data || { total: 0, public: 0, hidden: 0 });
        }
      })
      .catch(() => {
        if (active) {
          setFailed(true);
        }
      });
    return () => {
      active = false;
    };
  }, [moduleId, refreshKey]);

  if (failed) {
    return <Text color="red.500">Erro</Text>;
  }
  if (!counts) {
    return <Spinner size="sm" />;
  }
  return (
    <Flex gap={2} wrap="wrap" data-testid={`module-counts-${moduleId}`}>
      <Badge colorScheme="blue">Total: {counts.total}</Badge>
      <Badge colorScheme="green">Públicos: {counts.public}</Badge>
      <Badge colorScheme="gray">Ocultos: {counts.hidden}</Badge>
    </Flex>
  );
}

export function ContentsModulesPage() {
  usePageTracking('modules_list');
  const [selectedAnalyticsModule, setSelectedAnalyticsModule] = useState<number | null>(null);
  const [selectedMaterialsModule, setSelectedMaterialsModule] = useState<any>(null);
  const [countsRefreshKey, setCountsRefreshKey] = useState(0);
  const baseUrl = '/v1/admin/classes';
  const { requestApi, data, loading, error } = useFetch(baseUrl);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { trackModuleView } = useAnalytics();
  const groupId = user?.group?.id;
  const canManageMaterials = groupId === 1 || groupId === 2;

  const columns = [
    { name: 'id', label: 'Id' },
    { name: 'name', label: 'Nome' },
    { name: 'status', label: 'Status' },
    ...(canManageMaterials
      ? [
          { name: 'material_counts', label: 'Materiais' },
          { name: 'manage_materials', label: 'Ordem e visibilidade' },
        ]
      : []),
    { name: 'weeks', label: 'Semanas' },
    { name: 'stats', label: 'Estatísticas' },
  ];

  const openAnalytics = (moduleId: number) => {
    trackModuleView(moduleId);
    setSelectedAnalyticsModule(moduleId);
  };

  return (
    <Box>
      <PageTitle>Módulos</PageTitle>
      <DataGrid
        columns={columns}
        rows={data?.data}
        requestApi={requestApi}
        error={error}
        baseUrl={baseUrl}
        data={data}
        hasCreate={groupId !== 7 && groupId !== 3 && groupId !== 6}
        loading={loading}
        rowActions={{
          show: false,
          edit: groupId !== 7 && groupId !== 6,
          delete: groupId !== 7 && groupId !== 6 && groupId !== 3,
        }}
        columnWithAction={[
          ...(canManageMaterials
            ? [
                {
                  name: 'material_counts',
                  columnAction: (rowItem: any) => (
                    <ModuleCounts moduleId={rowItem.id} refreshKey={countsRefreshKey} />
                  ),
                },
                {
                  name: 'manage_materials',
                  columnAction: (rowItem: any) => (
                    <Button
                      leftIcon={<ListDashes />}
                      size="sm"
                      onClick={() => setSelectedMaterialsModule(rowItem)}
                      data-testid={`manage-materials-${rowItem.id}`}
                    >
                      Gerenciar
                    </Button>
                  ),
                },
              ]
            : []),
          {
            name: 'weeks',
            columnAction: (rowItem: any) => (
              <Button colorScheme="button.primary" onClick={() => navigate(`${rowItem.id}/weeks`)}>
                Ver semanas
              </Button>
            ),
          },
          {
            name: 'stats',
            columnAction: (rowItem: any) => (
              <AnalyticsIcon
                tooltip="Ver estatísticas deste módulo"
                onClick={() => openAnalytics(rowItem.id)}
              />
            ),
          },
        ]}
        formatColum={[{ field: 'status', formatField: getCellStatus }]}
        headerFilters={[{ name: 'search', filterComponent: SearchFilter }]}
      />
      <AnalyticsModal
        isOpen={selectedAnalyticsModule !== null}
        onClose={() => setSelectedAnalyticsModule(null)}
        moduleId={selectedAnalyticsModule || undefined}
        type="module"
      />
      {canManageMaterials && (
        <ModuleMaterialsManager
          isOpen={selectedMaterialsModule !== null}
          moduleId={selectedMaterialsModule?.id || null}
          moduleName={selectedMaterialsModule?.name || ''}
          onClose={() => setSelectedMaterialsModule(null)}
          onCountsChanged={() => setCountsRefreshKey((current) => current + 1)}
        />
      )}
    </Box>
  );
}
